import { createAsyncThunk } from "@reduxjs/toolkit";
import api, { API_PATHS } from "../../service/api";
import { resetSolutionsOnPlanningDate } from "../nexusFilters";
import { AxiosError } from "axios";
import { resetPondooViewState, setViewStateError } from "../realTimeUpdates";
import labels from "../../utils/labels";
import {
  GanttChartData,
  type EmployeeTransport,
  type TransportationType,
} from "../../types/ganttChart";
import RequestCancellationService from "../../service/requestCancellationService";
import { ScenarioService } from "../../service/ScenarioService";
import { PlanViewKey } from "../../hooks/useQueryParams";

enum actionTypes {
  initiateGanttChartData = "initiateGanttChartData ",
  initiateOptimization = "initiateOptimization ",
  stopOptimization = "stopOptimization",
  getGanttChartData = "getGanttChartData ",
  getPondooStatus = "getPondooStatus",
  initiateNexusLandingDataExtraction = "initiateNexusLandingDataExtraction",
  updatePondooStatus = "updatePondooStatus",
  getCustomPlan = "getCustomPlan",
}

// The backend still returns the old single-string transportationType format.
// Normalise to EmployeeTransport[] so all read sites get an array.
const normalizeTransportType = (data: GanttChartData): GanttChartData => {
  // Escape hatch: if every employee already has an array transportationType,
  // the input conforms and there's nothing to normalize — pass it through.
  const isAlreadyNormalized = data.employees.every((employee) =>
    Array.isArray(employee.transportationType),
  );
  if (isAlreadyNormalized) return data;

  data.employees = data.employees.map((employee) => {
    const tt: unknown = employee.transportationType;
    if (Array.isArray(tt)) return employee;
    const segment: EmployeeTransport = {
      start: employee.shiftStartTime,
      end: employee.shiftEndTime,
      transportationType:
        typeof tt === "string" ? (tt as TransportationType) : "CAR",
    };
    return { ...employee, transportationType: [segment] };
  });
  return data;
};

type InitiateGanttChartDataParams = {
  filterId: number;
  date: string;
};

type GetPondooStatusParams = {
  idActiveNexusFilter: number;
  planningDate: string;
  lastModified: string;
};

export type DataExtractionResponse = {
  status: number;
  idScenario?: number;
  message: string;
  jobId: string;
};

export const initiateGanttChartData = createAsyncThunk(
  actionTypes.initiateGanttChartData,
  async (params: InitiateGanttChartDataParams, thunkAPI) => {
    const data = {
      idActiveNexusFilter: params.filterId,
      planningDate: params.date.split("T")[0],
    };
    const response = await api.post<DataExtractionResponse>(
      API_PATHS.initiateNexusPlan,
      data,
    );
    if (response.status === 200) {
      // Reset IDs on that particular planning date
      thunkAPI.dispatch(resetSolutionsOnPlanningDate(params.date));
    }
    return response.data;
  },
);

export const initiateNexusLandingDataExtraction = createAsyncThunk(
  actionTypes.initiateNexusLandingDataExtraction,
  async (
    params: InitiateGanttChartDataParams & {
      pondooFilterId: number;
      filterName: string;
      resourceIds: string;
    },
  ) => {
    const response = await api.post<any>(API_PATHS.nexusLanding, {
      filterId: params.filterId,
      date: params.date.split("T")[0],
      filterName: params.filterName,
      resourceIds: params.resourceIds,
    });
    return response.data;
  },
);

export const initiateOptimization = createAsyncThunk(
  actionTypes.initiateOptimization,
  async (param: number) => {
    const data = {
      idScenario: param,
    };
    const response = await api.post<any>(API_PATHS.startOptimization, data);
    return response.data;
  },
);

export const stopOptimization = createAsyncThunk(
  actionTypes.stopOptimization,
  async (param: number) => {
    const data = {
      idScenario: param,
    };
    const response = await api.post<any>(API_PATHS.stopOptimization, data);
    return response.data;
  },
);

export const getGanttChartData = createAsyncThunk(
  actionTypes.getGanttChartData,
  async (params: PlanViewKey, thunkAPI) => {
    const sanitizedDate = params.date!.split("T")[0];
    const requestService = RequestCancellationService.getInstance();

    // Create unique key for this request type
    const requestKey = `gantt-${params.filterId}-${params.date}`;

    // Create new controller and register it (this will cancel any existing one)
    const controller = new AbortController();
    requestService.addRequest(requestKey, controller);

 try {
      const response = await api.get<GanttChartData>(
        `${API_PATHS.getNexusPlan}?idActiveNexusFilter=${params.filterId}&date=${sanitizedDate}&pondooSolution=${params.solutionType === "PondooSolution"}`,
        {
          signal: controller.signal,
        },
      );

      // Clean up on success
      requestService.removeRequest(requestKey);

      // Stash scenario id so the axios interceptor can attach X-Scenario-Id
      // on subsequent point-mutation requests; see ScenarioService.
      if (response.data?.idScenario != null) {
        ScenarioService.getInstance().setIdScenario(response.data.idScenario);
      }

      return normalizeTransportType(response.data);
    } catch (error: AxiosError | unknown) {
      // Clean up on error
      requestService.removeRequest(requestKey);

      // Handle abort error
      if (error instanceof Error && error.name === "AbortError") {
        return thunkAPI.rejectWithValue("Request cancelled");
      }

      const { response } = error as AxiosError;
      if (response?.status === 404) {
        if (params.solutionType === "PondooSolution") {
          thunkAPI.dispatch(resetPondooViewState(params));
        } else {
          thunkAPI.dispatch(
            // ask Nico
            // initiateGanttChartData({
            //   filterId: params.filterId!,
            //   date: sanitizedDate,

            setViewStateError({
              key: params,
              errorMessage: labels.pondooNexusLandingError,
            }),
          );
        }
      }
      throw error;
    }
  },
);

export const getPondooStatus = createAsyncThunk(
  actionTypes.getPondooStatus,
  async (params: GetPondooStatusParams) => {
    try {
      const response = await api.get<any>(
        `${API_PATHS.retrievePondooStatus}?idActiveNexusFilter=${params.idActiveNexusFilter}&fullState=true&planningDate=${params.planningDate}`,
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  },
);
